/**
 * Axios interceptors with Sentry integration for automatic error tracking
 * This provides global API monitoring without needing to instrument individual calls
 */
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { addBreadcrumb, captureException, logger } from "utils/sentry";
import { getCleanUrl, getServiceName } from "utils/sentryUrlHelpers";

import { markAsApiRequest } from "api/apiRequestHeaders";
import { isLocationLookupUrl } from "api/map";

/**
 * Calculates response size safely without stringifying large objects
 */
function getResponseSize(data: unknown): number {
  if (!data) return 0;

  try {
    // For arrays, estimate based on length
    if (Array.isArray(data)) {
      return data.length * 100; // Rough estimate
    }

    // For objects, estimate based on key count
    if (typeof data === "object") {
      return Object.keys(data).length * 50; // Rough estimate
    }

    // For primitives, get actual size
    return JSON.stringify(data).length;
  } catch {
    return 0;
  }
}

/**
 * Determines if an HTTP status code warrants logging/tracking
 * @param statusCode - HTTP status code
 * @returns true if the error should be logged
 */
function shouldLogByStatusCode(statusCode?: number): boolean {
  if (!statusCode) return true; // Network errors (no status code) should be logged

  // Log server errors (5xx)
  if (statusCode >= 500) {
    return true;
  }

  // Log specific client errors that are usually actionable
  const actionableClientErrors = [400, 406, 422];
  if (actionableClientErrors.includes(statusCode)) {
    return true;
  }

  // Don't log other client errors (401, 403, 404, etc.)
  return false;
}

/** What the map service names in the body when its reference tables are not loaded. */
const REFERENCE_DATA_UNAVAILABLE = "reference data unavailable";

/**
 * The map service answers 503 with that body on a Jump to Location lookup when the
 * reference tables are not loaded in that environment. The UI already shows it as
 * "Location data unavailable", so it is an expected state, not an incident: without this
 * exclusion every user on an unloaded environment raises a Sentry error on every lookup.
 *
 * The body has to say so, though. A 503 from the gateway or the ingress means no map
 * service pod is ready at all, which is an incident on every route including these, and
 * that answer carries different words or none.
 */
function isReferenceDataUnavailable(data: unknown): boolean {
  const named = (data as { error?: unknown })?.error;
  if (typeof named === "string") {
    return named.toLowerCase() === REFERENCE_DATA_UNAVAILABLE;
  }
  // An unparsed body still counts when it carries the service's own wording, and a
  // gateway's HTML or empty answer does not.
  return (
    typeof data === "string" && data.toLowerCase().includes(REFERENCE_DATA_UNAVAILABLE)
  );
}

function isExpectedLocationUnavailable(error: AxiosError): boolean {
  if (error.response?.status !== 503) return false;
  if (!isLocationLookupUrl(error.config?.url)) return false;
  return isReferenceDataUnavailable(error.response.data);
}

// Request interceptor to add Sentry tracking
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    markAsApiRequest(config.headers, config.url, config.baseURL);
    // Add breadcrumb for all outgoing requests
    const serviceName = getServiceName(config.url);
    const breadcrumbData: Record<string, string | undefined> = {
      url: getCleanUrl(config.url),
      method: config.method
    };

    // Only add service_name if it's a same-site request
    if (serviceName) {
      breadcrumbData.service_name = serviceName;
    }

    addBreadcrumb({
      message: `API Request: ${config.method?.toUpperCase()} ${getCleanUrl(config.url)}`,
      category: "http",
      level: "info",
      data: breadcrumbData
    });

    return config;
  },
  (error: AxiosError) => {
    // Capture request setup errors
    captureException(error, {
      tags: {
        error_type: "request_setup",
        operation: "axios_request_interceptor"
      }
    });

    logger.error("API request setup failed", {
      error: error.message
    });

    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
axios.interceptors.response.use(
  (response: AxiosResponse) => {
    // Add breadcrumb for successful responses
    addBreadcrumb({
      message: `API Response: ${
        response.status
      } ${response.config.method?.toUpperCase()} ${getCleanUrl(response.config.url)}`,
      category: "http",
      level: "info",
      data: {
        status: response.status,
        url: getCleanUrl(response.config.url),
        method: response.config.method,
        responseSize: getResponseSize(response.data)
      }
    });

    return response;
  },
  (error: AxiosError) => {
    // Add breadcrumb for failed responses
    addBreadcrumb({
      message: `API Error: ${
        error.response?.status || "Network Error"
      } ${error.config?.method?.toUpperCase()} ${getCleanUrl(error.config?.url)}`,
      category: "http",
      level: "error",
      data: {
        status: error.response?.status,
        url: getCleanUrl(error.config?.url),
        method: error.config?.method,
        errorMessage: error.message
      }
    });

    // Only log errors that warrant attention
    const shouldLogError =
      shouldLogByStatusCode(error.response?.status) &&
      !isExpectedLocationUnavailable(error);

    if (shouldLogError) {
      logger.error(
        logger.fmt`API request failed: ${error.config?.method?.toUpperCase()} ${getCleanUrl(
          error.config?.url
        )}`,
        {
          status: error.response?.status,
          method: error.config?.method,
          url: getCleanUrl(error.config?.url),
          error: error.message
        }
      );

      // Get service name for better error context
      const serviceName = getServiceName(error.config?.url);
      const statusCode = error.response?.status;
      const method = error.config?.method?.toUpperCase();

      // Create a more descriptive error message for Sentry
      const enhancedError = new Error(error.message);
      enhancedError.name = serviceName ? `${serviceName} API Error` : "API Error";
      enhancedError.message = serviceName
        ? `[${serviceName}] ${method} request failed with status ${statusCode}`
        : `${method} request failed with status ${statusCode}`;
      enhancedError.stack = error.stack;

      captureException(enhancedError, {
        tags: {
          error_type: "api_error",
          operation: "http_request",
          status_code: statusCode?.toString(),
          service_name: serviceName
        },
        contexts: {
          api_request: {
            url: error.config?.url,
            method: error.config?.method,
            status: statusCode
          },
          original_error: {
            name: error.name,
            message: error.message,
            code: error.code
          }
        },
        fingerprint: [
          error.config?.method || "unknown_method",
          getCleanUrl(error.config?.url),
          statusCode?.toString() || "network_error",
          ...(serviceName ? [serviceName] : [])
        ]
      });
    }

    return Promise.reject(error);
  }
);
